/* ============================================================
   AVORA HOME — Product Page JavaScript
   All product page interactions: gallery, lightbox, variants,
   quantity, add-to-cart, sticky bar, accordions, recently viewed,
   recommendations, bought together.
   ============================================================ */

(function() {
  'use strict';

  document.addEventListener('DOMContentLoaded', function() {
    initGalleryV2();
    initLightbox();
    initVariantSelectorV2();
    initQuantitySelector();
    initAddToCart();
    initBuyNow();
    initNewsletterDiscountCTA();
    initStickyBar();
    initProductAccordion();
    initFaqAccordion();
    initRecentlyViewed();
    initRecommendations();
    initBoughtTogether();
    initThumbsOverflow();
  });

  /* ----------------------------------------------------------
     HELPERS
     ---------------------------------------------------------- */

  /**
   * Format price in cents using Shopify's money_format template.
   * Matches the Liquid | money filter output exactly.
   * Shopify stores prices in cents (e.g. 3999 = €39.99).
   */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var moneyFormat = window.__shopMoneyFormat;
    var tokenMatch = typeof moneyFormat === 'string' ? moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/) : null;

    if (tokenMatch) {
      var token = tokenMatch[1];
      var precision = token.indexOf('no_decimals') > -1 ? 0 : 2;
      var thousands = token.indexOf('comma_separator') > -1 ? '.' : ',';
      var decimal = token.indexOf('comma_separator') > -1 ? ',' : '.';

      if (token.indexOf('apostrophe_separator') > -1) {
        thousands = "'";
        decimal = '.';
      }

      var amount = (cents / 100).toFixed(precision);
      var parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var formatted = precision > 0 ? parts[0] + decimal + parts[1] : parts[0];
      return moneyFormat.replace(tokenMatch[0], formatted);
    }

    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || window.__shopCurrencyCode || 'EUR';
    var locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /** Return the checkout-backed sale price using Shopify's discount rounding. */
  function dailySalePrice(cents, percentage) {
    if (typeof cents !== 'number' || isNaN(cents)) return cents;
    if (typeof percentage !== 'number' || isNaN(percentage) || percentage <= 0) return cents;
    return cents - Math.floor(cents * percentage / 100);
  }

  /**
   * Safely parse JSON from a script tag by selector.
   * Returns parsed value or fallback on failure.
   */
  function parseJSON(selector, fallback) {
    var el = document.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  /**
   * Update cart count badges in the header.
   */
  function updateCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        var badges = document.querySelectorAll('.cart-count-badge, [data-cart-count], .cart-bubble__text-count');
        badges.forEach(function(b) {
          b.textContent = cart.item_count;
          if (cart.item_count > 0) {
            b.style.display = '';
            b.classList.remove('hidden');
            var bubble = b.closest('.cart-bubble');
            if (bubble) bubble.classList.remove('visually-hidden');
          }
        });
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: { itemCount: cart.item_count, source: 'avora-product-page', mode: 'absolute' }
          }
        }));
        return cart;
      })
      .catch(function() { return null; });
  }

  /* ----------------------------------------------------------
     NEWSLETTER DISCOUNT CTA
     ---------------------------------------------------------- */
  function initNewsletterDiscountCTA() {
    var triggers = document.querySelectorAll('[data-avora-newsletter-discount-trigger]');
    if (!triggers.length) return;

    function revealTriggers() {
      triggers.forEach(function(trigger) {
        trigger.hidden = false;
      });
    }

    if (window.AvoraWelcomePopup && typeof window.AvoraWelcomePopup.open === 'function') {
      revealTriggers();
    } else {
      window.addEventListener('avora:welcome-popup:ready', revealTriggers, { once: true });
    }

    triggers.forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        if (window.AvoraWelcomePopup && typeof window.AvoraWelcomePopup.open === 'function') {
          window.AvoraWelcomePopup.open();
        } else {
          document.dispatchEvent(new CustomEvent('avora:welcome-popup:open'));
        }
      });
    });
  }

  /* ----------------------------------------------------------
     GALLERY V2
     ---------------------------------------------------------- */
  function initGalleryV2() {
    var mainContainer = document.querySelector('.avora-gallery__main');
    if (!mainContainer) return;

    var stage = mainContainer.querySelector('.avora-gallery__image-stage');
    var mainImg = mainContainer.querySelector('.avora-gallery__main-image');
    if (!stage || !mainImg) return;

    var images = parseJSON('#avora-product-images-json', []);
    var thumbs = document.querySelectorAll('.avora-gallery__thumb');
    var prevBtn = mainContainer.querySelector('.avora-gallery__arrow--prev');
    var nextBtn = mainContainer.querySelector('.avora-gallery__arrow--next');

    var currentIndex = parseInt(stage.getAttribute('data-current-index'), 10) || 0;
    var isTransitioning = false;

    function crossfadeTo(index, newSrc) {
      if (isTransitioning) return;
      if (index === currentIndex && !newSrc) return;

      isTransitioning = true;
      var src = newSrc || (images[index] ? images[index].src : null);
      if (!src) { isTransitioning = false; return; }

      // Create overlay image for crossfade
      var fadeImg = document.createElement('img');
      fadeImg.className = 'avora-gallery__fade-image';
      fadeImg.alt = images[index] ? images[index].alt : '';
      fadeImg.src = src;

      stage.appendChild(fadeImg);

      function finishTransition() {
        mainImg.src = src;
        mainImg.alt = fadeImg.alt;
        if (fadeImg.parentNode) fadeImg.parentNode.removeChild(fadeImg);
        currentIndex = index;
        stage.setAttribute('data-current-index', index);
        isTransitioning = false;

        // Sync lightbox
        if (window._avoraLightbox) {
          window._avoraLightbox.currentIndex = index;
        }
      }

      // Wait for load then animate
      function doFade() {
        requestAnimationFrame(function() {
          fadeImg.classList.add('is-visible');
          setTimeout(finishTransition, 320);
        });
      }

      if (fadeImg.complete) {
        doFade();
      } else {
        fadeImg.addEventListener('load', doFade);
        fadeImg.addEventListener('error', function() {
          if (fadeImg.parentNode) fadeImg.parentNode.removeChild(fadeImg);
          isTransitioning = false;
        });
      }

      // Update thumbs active state
      thumbs.forEach(function(t) { t.classList.remove('active'); });
      if (thumbs[index]) thumbs[index].classList.add('active');
    }

    // Arrow clicks
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        var newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = images.length - 1;
        crossfadeTo(newIndex);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        var newIndex = currentIndex + 1;
        if (newIndex >= images.length) newIndex = 0;
        crossfadeTo(newIndex);
      });
    }

    // Thumbnail clicks
    thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        var index = parseInt(thumb.getAttribute('data-index'), 10);
        if (isNaN(index)) return;
        crossfadeTo(index);
      });
    });

    // Mobile swipe on gallery
    var touchStartX = 0;
    var touchMoved = false;

    stage.addEventListener('touchstart', function(e) {
      touchStartX = e.touches[0].clientX;
      touchMoved = false;
    }, { passive: true });

    stage.addEventListener('touchmove', function() {
      touchMoved = true;
    }, { passive: true });

    stage.addEventListener('touchend', function(e) {
      if (!touchMoved) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) {
        if (dx < 0) {
          var ni = currentIndex + 1;
          if (ni >= images.length) ni = 0;
          crossfadeTo(ni);
        } else {
          var pi = currentIndex - 1;
          if (pi < 0) pi = images.length - 1;
          crossfadeTo(pi);
        }
      }
    }, { passive: true });

    // Expose crossfade for variant selector to use
    window._avoraGallery = {
      crossfadeTo: crossfadeTo,
      getCurrentIndex: function() { return currentIndex; },
      findImageIndex: function(src) {
        for (var i = 0; i < images.length; i++) {
          if (images[i].src === src) return i;
        }
        return -1;
      },
      findImageIndexById: function(imageId) {
        if (!imageId) return -1;
        for (var i = 0; i < images.length; i++) {
          if (images[i].id === imageId) return i;
        }
        return -1;
      },
      findImageIndexByUrl: function(src) {
        if (!src) return -1;
        // Strip query params and protocol for fuzzy matching
        var cleanSrc = src.replace(/^https?:/, '').split('?')[0];
        for (var i = 0; i < images.length; i++) {
          var cleanImg = images[i].src.replace(/^https?:/, '').split('?')[0];
          if (cleanImg === cleanSrc) return i;
        }
        return -1;
      }
    };
  }

  /* ----------------------------------------------------------
     THUMBNAIL OVERFLOW DETECTION
     ---------------------------------------------------------- */

  function initThumbsOverflow() {
    var wrap = document.querySelector('.avora-gallery__thumbs-wrap');
    var strip = wrap ? wrap.querySelector('.avora-gallery__thumbs') : null;
    if (!wrap || !strip) return;

    function checkOverflow() {
      if (strip.scrollWidth > strip.clientWidth + 2) {
        wrap.classList.add('has-overflow');
      } else {
        wrap.classList.remove('has-overflow');
      }
      updateScrollState();
    }

    function updateScrollState() {
      if (strip.scrollLeft <= 2) {
        wrap.classList.add('scrolled-start');
      } else {
        wrap.classList.remove('scrolled-start');
      }
      if (strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2) {
        wrap.classList.add('scrolled-end');
      } else {
        wrap.classList.remove('scrolled-end');
      }
    }

    strip.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', checkOverflow);
    checkOverflow();
  }

  /* ----------------------------------------------------------
     2. GALLERY — Lightbox
     ---------------------------------------------------------- */

  function initLightbox() {
    var lightbox = document.querySelector('.avora-lightbox');
    if (!lightbox) return;

    var mainGallery = document.querySelector('.avora-gallery__main');
    var lightboxImg = lightbox.querySelector('.avora-lightbox__image') || lightbox.querySelector('img');
    var closeBtn = lightbox.querySelector('.avora-lightbox__close');
    var arrowLeft = lightbox.querySelector('.avora-lightbox__arrow-left');
    var arrowRight = lightbox.querySelector('.avora-lightbox__arrow-right');

    // Build images array from thumbnails
    var images = [];
    var thumbs = document.querySelectorAll('.avora-gallery__thumb');
    thumbs.forEach(function(thumb) {
      var src = thumb.getAttribute('data-full-src');
      if (src) images.push(src);
    });

    // Fallback: if no thumbs, use the main image
    if (!images.length && mainGallery) {
      var mainImg = mainGallery.querySelector('img');
      if (mainImg && mainImg.src) images.push(mainImg.src);
    }

    // Shared lightbox state
    var state = {
      currentIndex: 0
    };
    window._avoraLightbox = state;

    function showImage(index) {
      if (!images.length) return;
      // Wrap around
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      state.currentIndex = index;

      if (lightboxImg) {
        lightboxImg.style.opacity = '0';
        lightboxImg.src = images[index];
        lightboxImg.addEventListener('load', function onLoad() {
          lightboxImg.style.opacity = '1';
          lightboxImg.removeEventListener('load', onLoad);
        });
        if (lightboxImg.complete) {
          lightboxImg.style.opacity = '1';
        }
      }

      // Update lightbox thumbnail strip if present
      var lbThumbs = lightbox.querySelectorAll('.avora-lightbox__thumb-btn');
      lbThumbs.forEach(function(t, i) {
        t.classList.toggle('active', i === index);
      });
    }

    function openLightbox() {
      lightbox.classList.add('active');
      document.body.style.overflow = 'hidden';
      showImage(state.currentIndex);
    }

    function closeLightbox() {
      lightbox.classList.remove('active');
      document.body.style.overflow = '';
    }

    // Open on main gallery click (but not when clicking arrows)
    if (mainGallery) {
      mainGallery.addEventListener('click', function(e) {
        if (e.target.closest('.avora-gallery__arrow') || e.target.closest('.avora-gallery__zoom-trigger')) return;
        // Sync index from gallery before opening
        if (window._avoraGallery) {
          state.currentIndex = window._avoraGallery.getCurrentIndex();
        }
        openLightbox();
      });

      var zoomBtn = mainGallery.querySelector('.avora-gallery__zoom-trigger');
      if (zoomBtn) {
        zoomBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          if (window._avoraGallery) {
            state.currentIndex = window._avoraGallery.getCurrentIndex();
          }
          openLightbox();
        });
      }
    }

    // Close
    if (closeBtn) {
      closeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        closeLightbox();
      });
    }

    // Close on overlay background click (not image, controls, or thumbs)
    lightbox.addEventListener('click', function(e) {
      if (e.target === lightbox || e.target.classList.contains('avora-lightbox__image-wrap')) {
        closeLightbox();
      }
    });

    // ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && lightbox.classList.contains('active')) {
        closeLightbox();
      }
    });

    // Arrow navigation
    if (arrowLeft) {
      arrowLeft.addEventListener('click', function(e) {
        e.stopPropagation();
        showImage(state.currentIndex - 1);
      });
    }
    if (arrowRight) {
      arrowRight.addEventListener('click', function(e) {
        e.stopPropagation();
        showImage(state.currentIndex + 1);
      });
    }

    // Keyboard arrow navigation while lightbox is open
    document.addEventListener('keydown', function(e) {
      if (!lightbox.classList.contains('active')) return;
      if (e.key === 'ArrowLeft') {
        showImage(state.currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        showImage(state.currentIndex + 1);
      }
    });

    // Mobile swipe support
    var touchStartX = 0;
    var touchStartY = 0;
    var touchMoved = false;

    lightbox.addEventListener('touchstart', function(e) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchMoved = false;
    }, { passive: true });

    lightbox.addEventListener('touchmove', function(e) {
      touchMoved = true;
    }, { passive: true });

    lightbox.addEventListener('touchend', function(e) {
      if (!touchMoved) return;
      var touchEndX = e.changedTouches[0].clientX;
      var touchEndY = e.changedTouches[0].clientY;
      var dx = touchEndX - touchStartX;
      var dy = touchEndY - touchStartY;

      // Only trigger if horizontal swipe is dominant and >= 50px
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        if (dx < 0) {
          showImage(state.currentIndex + 1); // swipe left → next
        } else {
          showImage(state.currentIndex - 1); // swipe right → prev
        }
      }
    }, { passive: true });

    // Lightbox thumbnail clicks
    var lbThumbs = lightbox.querySelectorAll('.avora-lightbox__thumb-btn');
    lbThumbs.forEach(function(t, i) {
      t.addEventListener('click', function(e) {
        e.stopPropagation();
        showImage(i);
      });
    });
  }

  /* ----------------------------------------------------------
     3. VARIANT SELECTOR V2 — Full redesign with multi-option support
     ---------------------------------------------------------- */

  function initVariantSelectorV2() {
    var variants = parseJSON('#avora-product-variants-json', []);
    var productOptions = parseJSON('#avora-product-options-json', []);
    if (!variants.length) return;

    var priceEl = document.querySelector('.avora-product-price');
    var atcButton = document.querySelector('.avora-btn-add-to-cart');
    var stickyAddBtn = document.querySelector('.avora-sticky-add-btn');
    var stickyPriceEl = document.querySelector('.avora-sticky-product-price');
    var buyNowBtn = document.querySelector('.avora-btn-buy-now');
    var dailySaleActive = priceEl && priceEl.getAttribute('data-daily-sale-active') === 'true';
    var dailySalePercentage = priceEl ? parseFloat(priceEl.getAttribute('data-daily-sale-percentage')) : 0;

    // Build ordered option names from product options
    var optionNames = [];
    productOptions.forEach(function(opt) {
      optionNames.push(opt.name);
    });

    // Track selected options — keyed by option name, in order
    var selectedOptions = {};

    // Initialize from is-selected buttons
    var allButtons = document.querySelectorAll('[data-option-name][data-option-value]');
    allButtons.forEach(function(btn) {
      if (btn.classList.contains('is-selected')) {
        selectedOptions[btn.getAttribute('data-option-name')] = btn.getAttribute('data-option-value');
      }
    });

    function findVariant() {
      return variants.find(function(v) {
        for (var i = 0; i < optionNames.length; i++) {
          var selectedVal = selectedOptions[optionNames[i]];
          if (selectedVal && v.options[i] !== selectedVal) {
            return false;
          }
        }
        return true;
      });
    }

    function updateForVariant(variant) {
      if (!variant) return;

      // Update price with animation
      if (priceEl) {
        var displayPrice = dailySaleActive ? dailySalePrice(variant.price, dailySalePercentage) : variant.price;
        var existingSaleTimer = priceEl.querySelector('.avora-sale-inline');
        var rowHTML = '<span class="avora-product-price__current">' + formatMoney(displayPrice) + '</span>';
        var savingsHTML = '';

        if (dailySaleActive) {
          rowHTML += '<span class="avora-product-price__compare">' + formatMoney(variant.price) + '</span>';
          rowHTML += '<span class="avora-product-price__badge">-' + dailySalePercentage + '%</span>';
          savingsHTML = '<div class="avora-product-price__savings">You save ' + formatMoney(variant.price - displayPrice) + '</div>';
        }

        priceEl.innerHTML = '<div class="avora-product-price__row">' + rowHTML + '</div>' + savingsHTML;
        if (existingSaleTimer) {
          priceEl.querySelector('.avora-product-price__row').appendChild(existingSaleTimer);
        }
        priceEl.classList.remove('is-updating');
        void priceEl.offsetWidth; // force reflow
        priceEl.classList.add('is-updating');
      }

      // Update sticky bar price
      if (stickyPriceEl) {
        stickyPriceEl.textContent = formatMoney(dailySaleActive ? dailySalePrice(variant.price, dailySalePercentage) : variant.price);
      }

      // Update variant ID on buttons
      if (atcButton) {
        atcButton.setAttribute('data-variant-id', variant.id);
      }
      if (buyNowBtn) {
        buyNowBtn.setAttribute('data-variant-id', variant.id);
      }

      // Update hidden variant input if present
      var hiddenInput = document.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
      }

      // Update ATC availability
      if (atcButton) {
        if (variant.available) {
          atcButton.disabled = false;
          var btnText = atcButton.querySelector('.avora-btn-text');
          if (btnText) {
            btnText.textContent = 'Add to Cart';
          }
          atcButton.classList.remove('soldout');
        } else {
          atcButton.disabled = true;
          var btnText2 = atcButton.querySelector('.avora-btn-text');
          if (btnText2) {
            btnText2.textContent = 'Sold Out';
          }
          atcButton.classList.add('soldout');
        }
      }

      // Update sticky add button
      if (stickyAddBtn) {
        stickyAddBtn.setAttribute('data-variant-id', variant.id);
        if (variant.available) {
          stickyAddBtn.disabled = false;
          stickyAddBtn.textContent = 'Add to Cart';
        } else {
          stickyAddBtn.disabled = true;
          stickyAddBtn.textContent = 'Sold Out';
        }
      }

      // Update Buy Now visibility
      if (buyNowBtn) {
        if (variant.available) {
          buyNowBtn.style.display = '';
          buyNowBtn.disabled = false;
          buyNowBtn.textContent = 'Buy It Now';
        } else {
          buyNowBtn.style.display = 'none';
        }
      }

      // Update gallery image via crossfade — match by ID first, then URL
      if (variant.featured_image && window._avoraGallery) {
        var imgIndex = -1;

        // Try matching by image ID (most reliable)
        if (variant.featured_image.id) {
          imgIndex = window._avoraGallery.findImageIndexById(variant.featured_image.id);
        }

        // Fallback: match by URL (strip query params for fuzzy match)
        if (imgIndex < 0 && variant.featured_image.src) {
          imgIndex = window._avoraGallery.findImageIndexByUrl(variant.featured_image.src);
        }

        // Exact URL match as last resort
        if (imgIndex < 0 && variant.featured_image.src) {
          imgIndex = window._avoraGallery.findImageIndex(variant.featured_image.src);
        }

        if (imgIndex >= 0) {
          window._avoraGallery.crossfadeTo(imgIndex);
        } else if (variant.featured_image.src) {
          // Image not in gallery array at all — crossfade directly to the src
          window._avoraGallery.crossfadeTo(window._avoraGallery.getCurrentIndex(), variant.featured_image.src);
        }
      }

      // Update URL with variant ID
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(new CustomEvent('avora:product-variant', {
        bubbles: true,
        detail: {
          variantId: variant.id,
          price: dailySaleActive ? dailySalePrice(variant.price, dailySalePercentage) : variant.price,
          available: !!variant.available,
          title: variant.title || ''
        }
      }));
    }

    // Bind all variant buttons (unified handler)
    allButtons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.classList.contains('is-soldout')) return;

        var optionName = btn.getAttribute('data-option-name');
        var optionValue = btn.getAttribute('data-option-value');
        if (!optionName || !optionValue) return;

        // Update selection within the same option group
        var group = btn.closest('.avora-variant-group');
        if (group) {
          group.querySelectorAll('[data-option-name][data-option-value]').forEach(function(b) {
            b.classList.remove('is-selected');
          });
        }
        btn.classList.add('is-selected');

        // Update selected options
        selectedOptions[optionName] = optionValue;

        // Update the color label if it's a color swatch
        if (group) {
          var labelSelected = group.querySelector('.avora-variant-label__selected');
          if (labelSelected) {
            labelSelected.textContent = optionValue;
          }
        }

        // Find and apply matching variant
        var variant = findVariant();
        if (variant) {
          updateForVariant(variant);
        }
      });
    });

    // Handle URL variant param on load
    var urlParams = new URLSearchParams(window.location.search);
    var urlVariantId = urlParams.get('variant');
    if (urlVariantId) {
      var urlVariant = variants.find(function(v) { return String(v.id) === String(urlVariantId); });
      if (urlVariant) {
        // Set selected options from the URL variant
        for (var i = 0; i < optionNames.length; i++) {
          if (urlVariant.options[i]) {
            selectedOptions[optionNames[i]] = urlVariant.options[i];
          }
        }

        // Update button states
        allButtons.forEach(function(btn) {
          var name = btn.getAttribute('data-option-name');
          var val = btn.getAttribute('data-option-value');
          if (selectedOptions[name] === val) {
            var group = btn.closest('.avora-variant-group');
            if (group) {
              group.querySelectorAll('[data-option-name][data-option-value]').forEach(function(b) {
                b.classList.remove('is-selected');
              });
            }
            btn.classList.add('is-selected');

            // Update label
            if (group) {
              var labelSel = group.querySelector('.avora-variant-label__selected');
              if (labelSel) labelSel.textContent = val;
            }
          }
        });

        updateForVariant(urlVariant);
      }
    }
  }

  /* ----------------------------------------------------------
     4. QUANTITY SELECTOR
     ---------------------------------------------------------- */

  function initQuantitySelector() {
    var qtyBtns = document.querySelectorAll('.avora-qty-btn');
    if (!qtyBtns.length) return;

    qtyBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var container = btn.closest('.avora-qty-selector');
        if (!container) return;

        var input = container.querySelector('.avora-qty-value');
        if (!input) return;

        var current = parseInt(input.value, 10) || 1;
        var action = btn.getAttribute('data-action');

        if (action === 'decrease') {
          if (current > 1) {
            input.value = current - 1;
          }
        } else if (action === 'increase') {
          if (current < 99) {
            input.value = current + 1;
          }
        }

        // Dispatch input event so other listeners can react
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Ensure manual input stays within range
    var qtyInputs = document.querySelectorAll('.avora-qty-value');
    qtyInputs.forEach(function(input) {
      input.addEventListener('change', function() {
        var val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) input.value = 1;
        if (val > 99) input.value = 99;
      });

      input.addEventListener('blur', function() {
        var val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) input.value = 1;
      });
    });
  }

  /* ----------------------------------------------------------
     5. ADD TO CART (with micro-interaction)
     ---------------------------------------------------------- */

  function initAddToCart() {
    var heroBtn = document.querySelector('.avora-btn-add-to-cart');
    var stickyBtn = document.querySelector('.avora-sticky-add-btn');

    if (heroBtn) {
      heroBtn.addEventListener('click', function(e) {
        e.preventDefault();
        addToCart(heroBtn);
      });
    }

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function(e) {
        e.preventDefault();
        addToCart(stickyBtn);
      });
    }
  }

  function addToCart(button) {
    if (button.disabled || button.classList.contains('loading')) return;

    // Get variant ID
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) {
      // Try hidden input
      var hiddenInput = document.querySelector('input[name="id"]');
      if (hiddenInput) variantId = hiddenInput.value;
    }
    if (!variantId) return;

    // Get quantity
    var qtyInput = document.querySelector('.avora-qty-value');
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    // Start animation — fade out text
    button.classList.add('loading');
    var btnText = button.querySelector('.avora-btn-text');
    var btnSpinner = button.querySelector('.avora-btn-spinner');
    var btnCheck = button.querySelector('.avora-btn-check');

    if (btnText) {
      btnText.style.transition = 'opacity 150ms ease';
      btnText.style.opacity = '0';
    }

    // Show spinner
    if (btnSpinner) {
      btnSpinner.style.display = 'flex';
      btnSpinner.style.opacity = '1';
    }

    // POST to cart
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: quantity }]
      })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(function() {
      // Hide spinner after 200ms, show checkmark
      setTimeout(function() {
        if (btnSpinner) {
          btnSpinner.style.opacity = '0';
          btnSpinner.style.display = 'none';
        }

        // Show checkmark with spring scale
        if (btnCheck) {
          btnCheck.style.display = 'flex';
          btnCheck.style.opacity = '1';
          btnCheck.style.transform = 'scale(1)';
        }

        // Success state
        button.classList.add('success');

        // Update cart count in header
        updateCartCount().then(function(cart) {
          if (window.avoraCartDrawer) {
            window.avoraCartDrawer.open(cart);
          }
        });

        // Trigger fly-to-cart animation
        if (window.avoraFlyToCart) {
          var heroImg = document.querySelector('.avora-gallery__main-image');
          var stickyThumb = document.querySelector('.avora-sticky-product-thumb');
          var imgEl = button.closest('.avora-sticky-cart-bar') ? stickyThumb : heroImg;
          var titleEl = document.querySelector('.avora-product-title');
          var priceEl2 = document.querySelector('.avora-product-price__current');
          window.avoraFlyToCart({
            imageEl: imgEl,
            imageSrc: imgEl ? imgEl.src : '',
            title: titleEl ? titleEl.textContent.trim() : '',
            price: priceEl2 ? priceEl2.textContent.trim() : '',
            sourceEl: button
          });
        }

        // Revert after 1.5s
        setTimeout(function() {
          button.classList.remove('success');
          button.classList.remove('loading');

          if (btnCheck) {
            btnCheck.style.opacity = '0';
            btnCheck.style.transform = 'scale(0.5)';
            btnCheck.style.display = 'none';
          }

          if (btnText) {
            btnText.style.opacity = '1';
          }
        }, 1500);
      }, 200);
    })
    .catch(function() {
      // Error state
      if (btnSpinner) {
        btnSpinner.style.opacity = '0';
        btnSpinner.style.display = 'none';
      }

      if (btnText) {
        btnText.style.opacity = '1';
        var originalText = btnText.textContent;
        btnText.textContent = 'Error \u2014 retry';

        setTimeout(function() {
          btnText.textContent = originalText;
          button.classList.remove('loading');
        }, 2000);
      } else {
        var origText = button.textContent;
        button.textContent = 'Error \u2014 retry';
        setTimeout(function() {
          button.textContent = origText;
          button.classList.remove('loading');
        }, 2000);
      }

    });
  }

  /* ----------------------------------------------------------
     6. BUY NOW
     ---------------------------------------------------------- */

  function initBuyNow() {
    var buyBtn = document.querySelector('.avora-btn-buy-now');
    if (!buyBtn) return;

    buyBtn.addEventListener('click', function(e) {
      e.preventDefault();

      var variantId = document.querySelector('.avora-btn-add-to-cart');
      var vid = variantId ? variantId.getAttribute('data-variant-id') : null;
      if (!vid) {
        var hiddenInput = document.querySelector('input[name="id"]');
        if (hiddenInput) vid = hiddenInput.value;
      }
      if (!vid) return;

      var qtyInput = document.querySelector('.avora-qty-value');
      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      // Direct checkout — does NOT touch the main cart
      buyBtn.disabled = true;
      buyBtn.textContent = 'Redirecting…';
      var cartPath = (window.routes && window.routes.cart_url) || '/cart';
      window.location.href = cartPath + '/' + vid + ':' + quantity + '?checkout';
    });
  }

  /* ----------------------------------------------------------
     7. STICKY CART BAR
     ---------------------------------------------------------- */

  function initStickyBar() {
    var heroATC = document.querySelector('.avora-btn-add-to-cart');
    var stickyBar = document.querySelector('.avora-sticky-cart-bar');
    if (!heroATC || !stickyBar) return;

    var footer = document.querySelector('footer') || document.querySelector('.avora-footer') || document.querySelector('[class*="footer"]');

    // Observe the hero ATC button
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          // Hero ATC is visible — hide sticky bar
          stickyBar.classList.remove('visible');
        } else {
          // Hero ATC is NOT visible — show sticky bar (unless near footer)
          if (footer) {
            var footerRect = footer.getBoundingClientRect();
            if (footerRect.top < window.innerHeight + 100) {
              stickyBar.classList.remove('visible');
              return;
            }
          }
          stickyBar.classList.add('visible');
        }
      });
    }, {
      threshold: 0,
      rootMargin: '0px'
    });

    observer.observe(heroATC);

    // Also hide near footer on scroll
    if (footer) {
      var scrollHandler = null;
      var ticking = false;

      scrollHandler = function() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function() {
          var footerRect = footer.getBoundingClientRect();
          if (footerRect.top < window.innerHeight + 100) {
            stickyBar.classList.remove('visible');
          }
          ticking = false;
        });
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
    }
  }

  /* ----------------------------------------------------------
     8. PRODUCT INFO ACCORDION (single open at a time)
     ---------------------------------------------------------- */

  function initProductAccordion() {
    var triggers = document.querySelectorAll('.avora-accordion__trigger');
    if (!triggers.length) return;

    triggers.forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        var item = trigger.closest('.avora-accordion__item');
        if (!item) return;

        var content = item.querySelector('.avora-accordion__content');
        if (!content) return;

        var isOpen = item.classList.contains('open');

        // Close all other items (single open behavior)
        var allItems = document.querySelectorAll('.avora-accordion__item');
        allItems.forEach(function(otherItem) {
          if (otherItem !== item && otherItem.classList.contains('open')) {
            otherItem.classList.remove('open');
            var otherContent = otherItem.querySelector('.avora-accordion__content');
            if (otherContent) {
              otherContent.style.maxHeight = '0';
            }
          }
        });

        // Toggle current item
        if (isOpen) {
          item.classList.remove('open');
          content.style.maxHeight = '0';
        } else {
          item.classList.add('open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });

    // Open first item by default if it has the class
    var firstOpen = document.querySelector('.avora-accordion__item.open');
    if (firstOpen) {
      var firstContent = firstOpen.querySelector('.avora-accordion__content');
      if (firstContent) {
        firstContent.style.maxHeight = firstContent.scrollHeight + 'px';
      }
    }
  }

  /* ----------------------------------------------------------
     9. FAQ ACCORDION (multiple can be open)
     ---------------------------------------------------------- */

  function initFaqAccordion() {
    var triggers = document.querySelectorAll('.avora-faq-trigger');
    if (!triggers.length) return;

    triggers.forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        var item = trigger.closest('.avora-faq-item');
        if (!item) return;

        var content = item.querySelector('.avora-faq-answer');
        if (!content) return;

        var isOpen = item.classList.contains('open');

        // Toggle current (multiple CAN be open)
        if (isOpen) {
          item.classList.remove('open');
          content.style.maxHeight = '0';
        } else {
          item.classList.add('open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }

  /* ----------------------------------------------------------
     10. RECENTLY VIEWED (localStorage)
     ---------------------------------------------------------- */

  function initRecentlyViewed() {
    var STORAGE_KEY = 'avora_recently_viewed';
    var MAX_STORED = 12;
    var DISPLAY_COUNT = 6;

    var section = document.querySelector('.avora-recently-viewed');
    var scrollContainer = document.querySelector('.avora-recently-viewed-scroll');

    // Get current product data
    var productDataEl = document.getElementById('avora-product-data');
    var handleEl = document.querySelector('[data-avora-product-handle]');
    var currentHandle = handleEl ? handleEl.getAttribute('data-avora-product-handle') : null;

    // Read stored items
    var stored = [];
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
      if (!Array.isArray(stored)) stored = [];
    } catch (e) {
      stored = [];
    }

    // Push current product into recently viewed
    if (currentHandle && productDataEl) {
      var productData = {
        handle: currentHandle,
        title: productDataEl.getAttribute('data-avora-product-title') || '',
        image_url: productDataEl.getAttribute('data-avora-product-image') || '',
        price: productDataEl.getAttribute('data-avora-product-price') || '',
        compare_price: productDataEl.getAttribute('data-avora-product-compare-price') || '',
        sale_percentage: productDataEl.getAttribute('data-avora-product-sale-percentage') || '',
        url: productDataEl.getAttribute('data-avora-product-url') || '/products/' + currentHandle,
        variant_id: productDataEl.getAttribute('data-avora-product-variant-id') || ''
      };

      // Deduplicate: remove existing entry for this handle
      stored = stored.filter(function(item) {
        return item.handle !== currentHandle;
      });

      // Add current product to the front
      stored.unshift(productData);

      // Cap at max
      if (stored.length > MAX_STORED) {
        stored = stored.slice(0, MAX_STORED);
      }

      // Save
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (e) {
        // localStorage full or unavailable
      }
    }

    // Display items (excluding current product)
    var displayItems = stored.filter(function(item) {
      return item.handle !== currentHandle;
    }).slice(0, DISPLAY_COUNT);

    // If fewer than 2 items, hide section
    if (displayItems.length < 2) {
      if (section) section.style.display = 'none';
      return;
    }

    // Render Avora cards
    if (scrollContainer) {
      var html = '';
      displayItems.forEach(function(item) {
        html += buildAvoraCardHTML({
          url: item.url,
          title: item.title,
          imageUrl: item.image_url,
          price: item.price,
          comparePrice: item.compare_price || '',
          salePercentage: item.sale_percentage || '',
          handle: item.handle || '',
          variantId: item.variant_id || ''
        });
      });
      scrollContainer.innerHTML = html;
      reinitGlassCardQuickAdd(scrollContainer);
    }

    if (section) {
      section.style.display = '';
    }
  }

  /**
   * Re-init quick-add click handlers on JS-injected glass cards.
   * Mirrors the event listener from avora-collection-card.liquid.
   */
  function reinitGlassCardQuickAdd(container) {
    container.querySelectorAll('.avora-card__quick-add').forEach(function(btn) {
      if (btn.dataset.avoraInit) return;
      btn.dataset.avoraInit = 'true';

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;

        var hasOnlyDefault = btn.dataset.hasOnlyDefault === 'true';
        if (!hasOnlyDefault) {
          window.location.href = btn.dataset.productUrl;
          return;
        }

        var variantId = btn.dataset.variantId;
        btn.style.pointerEvents = 'none';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Cart error');
          return res.json();
        })
        .then(function() {
          btn.classList.add('success');
          if (window.avoraFlyToCart) {
            var card = btn.closest('.avora-card');
            var imgEl = card ? card.querySelector('img') : null;
            window.avoraFlyToCart({
              imageEl: imgEl,
              imageSrc: imgEl ? imgEl.src : '',
              title: btn.getAttribute('aria-label') || '',
              price: '',
              sourceEl: btn
            });
          }
          fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
            document.querySelectorAll('.cart-count-badge, [data-cart-count], .cart-bubble__text-count').forEach(function(el) {
              el.textContent = cart.item_count;
            });
          });
          setTimeout(function() {
            btn.classList.remove('success');
            btn.style.pointerEvents = '';
          }, 1500);
        })
        .catch(function() {
          btn.style.pointerEvents = '';
        });
      });
    });
  }

  function escapeHTML(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /**
   * Build Avora floating glass overlay card HTML (used by JS-rendered sections).
   * Matches snippets/avora-collection-card.liquid markup.
   */
  function buildAvoraCardHTML(opts) {
    var url = escapeHTML(opts.url || '');
    var title = escapeHTML(opts.title || '');
    var imageUrl = escapeHTML(opts.imageUrl || '');
    var price = escapeHTML(opts.price || '');
    var comparePrice = escapeHTML(opts.comparePrice || '');
    var salePercentage = escapeHTML(String(opts.salePercentage || ''));
    var variantId = opts.variantId || '';
    var handle = escapeHTML(opts.handle || '');
    var available = opts.available !== false;
    var hasOnlyDefault = opts.hasOnlyDefaultVariant !== false;

    var html = '<div class="avora-card" data-product-handle="' + handle + '">';
    html += '<a href="' + url + '" class="avora-card__link" aria-label="' + title + '">';

    /* Image */
    html += '<div class="avora-card__image-wrap">';
    if (imageUrl) {
      html += '<img src="' + imageUrl + '" alt="' + title + '" loading="lazy" width="600">';
    }
    if (salePercentage) {
      html += '<span class="avora-card__badge avora-card__badge--sale">-' + salePercentage + '%</span>';
    }
    html += '</div>';

    /* Floating glass info panel */
    html += '<div class="avora-card__glass-info">';
    html += '<h3 class="avora-card__title">' + title + '</h3>';
    html += '<div class="avora-card__price-row">';
    html += '<span class="avora-card__price">' + price;
    if (comparePrice) {
      html += '<span class="avora-card__price-compare">' + comparePrice + '</span>';
    }
    html += '</span>';
    /* Quick-add button */
    if (variantId && available) {
      html += '<button class="avora-card__quick-add"';
      html += ' data-variant-id="' + variantId + '"';
      html += ' data-has-only-default="' + hasOnlyDefault + '"';
      html += ' data-product-url="' + url + '"';
      html += ' aria-label="Add ' + title + ' to cart"';
      html += ' onclick="event.preventDefault();event.stopPropagation();">';
      html += '<svg class="avora-card__icon-plus" viewBox="0 0 16 16"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>';
      html += '<svg class="avora-card__icon-check" viewBox="0 0 16 16"><polyline points="3 8 7 12 13 4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      html += '</button>';
    }
    html += '</div>'; /* price-row */
    html += '</div>'; /* glass-info */

    html += '</a></div>';
    return html;
  }

  /* ----------------------------------------------------------
     11. PRODUCT RECOMMENDATIONS
     ---------------------------------------------------------- */

  function initRecommendations() {
    var container = document.querySelector('[data-avora-recommendations]');
    if (!container) return;

    var productId = container.getAttribute('data-product-id');
    if (!productId) return;

    var section = container.closest('.avora-recommendations') || container.parentElement;

    fetch('/recommendations/products.json?product_id=' + productId + '&limit=6', {
      credentials: 'same-origin'
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Recommendations failed');
      return res.json();
    })
    .then(function(data) {
      var products = data.products || [];
      if (products.length > 0) {
        renderRecommendationCards(container, products);
      } else {
        // Try fallback: collection products
        tryCollectionFallback(container, section);
      }
    })
    .catch(function() {
      // Try fallback: collection products
      tryCollectionFallback(container, section);
    });
  }

  function tryCollectionFallback(container, section) {
    var collectionData = parseJSON('#avora-collection-products-json', []);
    if (collectionData.length > 0) {
      renderRecommendationCards(container, collectionData);
    } else {
      // Hide section entirely
      if (section) section.style.display = 'none';
    }
  }

  function renderRecommendationCards(container, products) {
    var grid = container.querySelector('.avora-recommendations-grid');
    if (!grid) grid = container;

    var html = '';
    var productPriceEl = document.querySelector('.avora-product-price');
    var recommendationSaleActive = productPriceEl && productPriceEl.getAttribute('data-daily-sale-active') === 'true';
    var recommendationSalePercentage = productPriceEl ? parseFloat(productPriceEl.getAttribute('data-daily-sale-percentage')) : 0;
    products.forEach(function(product) {
      var imageUrl = '';
      var url = product.url || '/products/' + product.handle;

      if (product.featured_image) {
        imageUrl = typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image.url || product.featured_image.src || '';
      } else if (product.image) {
        imageUrl = typeof product.image === 'string'
          ? product.image
          : product.image.src || '';
      } else if (product.images && product.images.length) {
        var firstImg = product.images[0];
        imageUrl = typeof firstImg === 'string' ? firstImg : firstImg.src || '';
      }

      var price = '';
      var comparePrice = '';
      var rawPrice = null;
      if (typeof product.price === 'number') {
        rawPrice = product.price;
        price = formatMoney(product.price);
      } else if (typeof product.price === 'string') {
        price = product.price;
      } else if (product.price_min !== undefined) {
        rawPrice = product.price_min;
        price = 'From ' + formatMoney(product.price_min);
      }

      if (recommendationSaleActive && rawPrice !== null) {
        comparePrice = formatMoney(rawPrice);
        price = (product.price_min !== undefined && product.price === undefined ? 'From ' : '') + formatMoney(dailySalePrice(rawPrice, recommendationSalePercentage));
      }

      /* Get first variant ID for quick-add */
      var variantId = '';
      if (product.variants && product.variants.length) {
        variantId = product.variants[0].id;
      } else if (product.id) {
        variantId = product.id;
      }

      var title = product.title || '';

      html += buildAvoraCardHTML({
        url: url,
        title: title,
        imageUrl: imageUrl,
        price: price,
        comparePrice: comparePrice,
        salePercentage: recommendationSaleActive ? recommendationSalePercentage : '',
        variantId: variantId,
        handle: product.handle || '',
        available: product.available !== false,
        hasOnlyDefaultVariant: product.hasOnlyDefaultVariant !== false
      });
    });

    grid.innerHTML = html;

    // Show the section (it starts hidden with display:none)
    container.style.display = '';

    // Re-init quick-add handlers for newly injected cards
    reinitGlassCardQuickAdd(grid);
  }


  /* ----------------------------------------------------------
     13. BOUGHT TOGETHER — Redesigned Bundle
     ---------------------------------------------------------- */

  function initBoughtTogether() {
    var container = document.querySelector('.avora-fbt') || document.querySelector('.avora-bundle-v2') || document.querySelector('.avora-bundle');
    if (!container) return;

    var checkboxes = container.querySelectorAll('.avora-fbt__input, .avora-bundle__check-input');
    var totalEl = container.querySelector('[data-bundle-total]');
    var savingsEl = container.querySelector('[data-bundle-savings]');
    var addAllBtn = container.querySelector('[data-bundle-add-all]');
    var btnTextEl = addAllBtn ? addAllBtn.querySelector('.avora-fbt__bar-btn-text, .avora-bundle-v2__bar-btn-text') : null;

    if (!checkboxes.length) return;

    function recalculateTotal() {
      var total = 0;
      var checkedCount = 0;

      checkboxes.forEach(function(cb) {
        var slot = cb.closest('[data-bundle-item]');

        if (cb.checked) {
          var price = parseInt(cb.getAttribute('data-price'), 10);
          if (!isNaN(price)) total += price;
          checkedCount++;
          if (slot) slot.classList.remove('is-unchecked');
        } else {
          if (slot) slot.classList.add('is-unchecked');
        }
      });

      if (totalEl) {
        /* Animate price: fade out, update, fade in */
        totalEl.classList.add('is-animating');
        setTimeout(function() {
          totalEl.textContent = formatMoney(total);
          totalEl.classList.remove('is-animating');
        }, 180);
      }

      if (savingsEl) {
        savingsEl.classList.remove('is-visible');
      }

      if (addAllBtn) {
        addAllBtn.disabled = checkedCount < 1;
      }
    }

    checkboxes.forEach(function(cb) {
      cb.addEventListener('change', recalculateTotal);
    });

    recalculateTotal();

    if (addAllBtn) {
      addAllBtn.addEventListener('click', function(e) {
        e.preventDefault();

        var items = [];
        checkboxes.forEach(function(cb) {
          if (cb.checked) {
            var variantId = cb.getAttribute('data-variant-id');
            if (variantId) {
              items.push({ id: parseInt(variantId, 10), quantity: 1 });
            }
          }
        });

        if (!items.length) return;

        addAllBtn.disabled = true;
        if (!btnTextEl) btnTextEl = addAllBtn.querySelector('.avora-fbt__bar-btn-text');
        var originalText = btnTextEl ? btnTextEl.textContent : addAllBtn.textContent;
        if (btnTextEl) btnTextEl.textContent = 'Adding...';
        else addAllBtn.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Failed to add items');
          return res.json();
        })
        .then(function() {
          if (btnTextEl) btnTextEl.textContent = 'Added \u2713';
          else addAllBtn.textContent = 'Added \u2713';
          addAllBtn.classList.add('is-success');
          updateCartCount();

          /* Fly-to-cart with 200ms stagger */
          var flyDelay = 0;
          checkboxes.forEach(function(cb) {
            if (cb.checked) {
              var slot = cb.closest('[data-bundle-item]');
              if (slot && window.avoraFlyToCart) {
                var cardImg = slot.querySelector('img');
                var titleEl = slot.querySelector('.avora-card__title');
                var priceEl = slot.querySelector('.avora-card__price');
                (function(img, title, price, delay) {
                  setTimeout(function() {
                    window.avoraFlyToCart({
                      imageEl: img,
                      imageSrc: img ? img.src : '',
                      title: title ? title.textContent.trim() : '',
                      price: price ? price.textContent.trim() : '',
                      sourceEl: addAllBtn
                    });
                  }, delay);
                })(cardImg, titleEl, priceEl, flyDelay);
                flyDelay += 200;
              }
            }
          });

          setTimeout(function() {
            if (btnTextEl) btnTextEl.textContent = originalText;
            else addAllBtn.textContent = originalText;
            addAllBtn.classList.remove('is-success');
            addAllBtn.disabled = false;
          }, 2000);
        })
        .catch(function() {
          if (btnTextEl) btnTextEl.textContent = 'Error \u2014 retry';
          else addAllBtn.textContent = 'Error \u2014 retry';
          setTimeout(function() {
            if (btnTextEl) btnTextEl.textContent = originalText;
            else addAllBtn.textContent = originalText;
            addAllBtn.disabled = false;
          }, 2000);
        });
      });
    }
  }

})();
